import { createTheme } from "@mui/material/styles";
import { tokens as t } from "./tokens";

/**
 * Creates the shared PaperlessNOW MUI theme while keeping portal content inside
 * the caller's scoped baseline subtree.
 */
export const createPaperlessTheme = (portalId: string) => {
  const portalContainer = () => document.getElementById(portalId);

  return createTheme({
    palette: {
      primary: { main: t.color.primary, dark: t.color.primaryHover, contrastText: t.color.onPrimary },
      secondary: { main: t.color.tealOnInk },
      error: { main: t.color.bad },
      warning: { main: t.color.flag },
      success: { main: t.color.good },
      background: { default: t.color.canvas, paper: t.color.paper },
      text: { primary: t.color.inkText, secondary: t.color.subText },
      divider: t.color.cardLine,
    },
    shape: { borderRadius: t.radius.card },
    typography: {
      fontFamily: t.font.body,
      fontSize: 14,
      h4: { fontFamily: t.font.display, fontWeight: 600, fontSize: "1.75rem", letterSpacing: "-0.025em" },
      h5: { fontFamily: t.font.display, fontWeight: 600, fontSize: "1.25rem", letterSpacing: "-0.015em" },
      h6: { fontFamily: t.font.display, fontWeight: 600, fontSize: "1rem" },
      body1: { fontSize: "0.925rem", lineHeight: 1.65 },
      body2: { fontSize: "0.8125rem", lineHeight: 1.6 },
      button: { textTransform: "none", fontWeight: 600 },
      overline: {
        fontFamily: t.font.body,
        fontWeight: 600,
        fontSize: "0.625rem",
        letterSpacing: "0.12em",
        lineHeight: 2,
      },
    },
    components: {
      MuiScopedCssBaseline: {
        styleOverrides: {
          root: {
            backgroundColor: t.color.canvas,
            color: t.color.inkText,
            fontFamily: t.font.body,
          },
        },
      },
      MuiModal: { defaultProps: { container: portalContainer } },
      MuiPopover: { defaultProps: { container: portalContainer } },
      MuiMenu: { defaultProps: { container: portalContainer } },
      MuiButtonBase: { styleOverrides: { root: { fontFamily: t.font.body } } },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: {
            borderRadius: t.radius.control,
            minHeight: 38,
            paddingLeft: 16,
            paddingRight: 16,
            "&.Mui-focusVisible": { boxShadow: `0 0 0 3px ${t.color.focusRing}` },
          },
          containedPrimary: { "&:hover": { backgroundColor: t.color.primaryHover } },
        },
      },
      MuiPaper: {
        defaultProps: { elevation: 0 },
        styleOverrides: { root: { backgroundImage: "none" } },
      },
      MuiTextField: { defaultProps: { size: "small", fullWidth: true } },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            backgroundColor: t.color.inputBg,
            borderRadius: t.radius.control,
            "& .MuiOutlinedInput-notchedOutline": { borderColor: t.color.inputLine },
            "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: t.color.subText },
            "&.Mui-focused": { boxShadow: `0 0 0 3px ${t.color.focusRing}` },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: t.color.primary,
              borderWidth: 1,
            },
          },
        },
      },
      MuiFormLabel: {
        styleOverrides: {
          root: {
            fontFamily: t.font.body,
            fontSize: "0.75rem",
            fontWeight: 600,
            color: t.color.labelText,
            "&.Mui-focused": { color: t.color.primary },
          },
        },
      },
      MuiFormHelperText: { styleOverrides: { root: { marginLeft: 0 } } },
      MuiTabs: {
        styleOverrides: {
          root: { borderBottom: `1px solid ${t.color.cardLine}`, minHeight: 44 },
          indicator: { height: 3, borderRadius: "3px 3px 0 0", backgroundColor: t.color.primary },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: "none",
            fontWeight: 500,
            fontSize: "0.8125rem",
            minHeight: 44,
            padding: "10px 16px",
            "&.Mui-selected": { fontWeight: 600 },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: { borderBottom: `1px solid ${t.color.rowLine}`, padding: "8px 16px" },
          head: {
            fontFamily: t.font.body,
            fontSize: "0.6875rem",
            fontWeight: 600,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            color: t.color.subText,
          },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: { fontFamily: t.font.display, fontWeight: 600, fontSize: "1.05rem" },
        },
      },
    },
  });
};
